import React from 'react';

/** 船图（实拍集装箱船，原生 300×74，按 112px 宽显示）——与 public/scripts/loading.js 同一份静态资源 */
const SHIP_SRC = '/loading-ship.png';
/** 海面航迹线：viewBox 的 260×60 坐标系，x 方向由 preserveAspectRatio="none" 拉满舞台宽度 */
const SEA_PATH = 'M0,32 L260,32';

/**
 * 船图尺寸 —— 与 appSplash.css 的 --ls-ship-w 同值，只作**零样式兜底**用：
 * 写成 <img> 的 width/height 属性（presentational hint，优先级低于任何 CSS），
 * 正常路径下永远被 width:var(--ls-ship-w) + aspect-ratio:300/74 覆盖。
 *
 * 为什么必须有：.ls-splash 的全部规则只存在于入口样式表 umi.<hash>.css
 * （global.less → styles/appSplash.css）。这张表没拿到时（重建后哈希周转，或
 * SPA fallback 把 .css 请求回成 text/html、被浏览器按 MIME 不符整张丢弃），
 * 船的 width / aspect-ratio / opacity 会一起消失 → 退回原生 300×74 且全不透明，
 * 落在文档流左上角，看起来就是「白屏 + 页面最左侧一条很大的船」。
 * boot 那一份不受影响（public/scripts/loading.js 自带内联样式），所以这一侧要自己兜住。
 *
 * 高度按原生比例推：112 × 74 ÷ 300 ≈ 27.6 → 28。取整带来的 1.35% 比例差只在
 * 「样式表缺席」这一种情况下可见，正常渲染由 CSS 的 aspect-ratio 决定，与它无关。
 */
const SHIP_W = 112;
const SHIP_H = 28;

/**
 * 零样式兜底 —— 兜「别散架」，不是设计的第二份副本。
 * 与 .ls-splash 同值（appSplashConsistency 回归测试会逐值比对，防止这份兜底自己漂移）；
 * 正常路径下每一条都被 appSplash.css 覆盖，仅在样式表缺席时生效 ——
 * 少了它，白屏期看到的就不是居中的启动动画，而是贴在左上角的原生尺寸船图。
 */
const SPLASH_FALLBACK_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '22px',
};

export interface AppSplashProps {
  /** 覆盖默认的动画与品牌字标，用于失败/慢速提示 */
  message?: React.ReactNode;
  /** 提示副文案 */
  subMessage?: React.ReactNode;
  /** 提供后渲染「刷新页面」按钮 */
  onRetry?: () => void;
  /** 自定义类名 */
  className?: string;
}

/** 船图缺失时摘掉元素，避免露出破图图标（航迹线仍在，画面完整） */
const hideBrokenShip = (event: React.SyntheticEvent<HTMLImageElement>) => {
  event.currentTarget.style.display = 'none';
};

/**
 * 相位接力 —— 首屏其实有两份同源动画先后登场：DOM 挂载前是
 * public/scripts/loading.js 往 #root 里插的占位节点，bundle 执行后换成这里。
 * 两者是各自独立的 DOM 元素，CSS 动画时钟各从 0 起算；不接力的话，交接那一瞬间
 * 船会从半路瞬间退回起点，而 ls-splash-sailfade 在周期前 14% 里 opacity 恒为 0，
 * 看起来就是「闪一下 + 船重新从起点出发」。
 *
 * 负 animation-delay 让动画直接从「已经跑过的时长」处开始，于是上一位跑到哪、
 * 这一位就从哪继续。这也是**任何**重挂载（React 重渲染、Suspense 重建）都不会
 * 让船重播的原因。
 */
const resolvePhaseDelay = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  // 旧版 headScript 里没有 splashPhase，读不到就退回 0 延迟（等于不接力）
  const phase = window.__dingyanPageLoading?.splashPhase?.();
  if (!phase || !phase.epoch || !phase.cycleMs) return undefined;
  return `-${((Date.now() - phase.epoch) % phase.cycleMs) / 1000}s`;
};

/**
 * 全屏启动加载动画 ——「航迹线性」（设计稿 SET D：一条航迹线 + 航行的集装箱船）。
 *
 * 唯一用途：**页面确实是空的**的时候（首屏启动、硬刷新、登录跳转前）。
 * 路由切换 / 菜单切换 / 表格取数一律不用它，避免整屏白闪。
 *
 * 与 public/scripts/loading.js 是同一套视觉：那边是 DOM 挂载前无法读取
 * 打包 CSS 的内联副本，改这里要同步改那边。
 */
export const AppSplash: React.FC<AppSplashProps> = ({
  message,
  subMessage,
  onRetry,
  className,
}) => {
  // 只在挂载时算一次：重渲染若重算，相位会被推着往前走，船会一顿一顿地跳
  const phaseDelay = React.useMemo(resolvePhaseDelay, []);

  return (
    <output
      className={className ? `ls-splash ${className}` : 'ls-splash'}
      style={
        (phaseDelay
          ? { ...SPLASH_FALLBACK_STYLE, '--ls-phase-delay': phaseDelay }
          : SPLASH_FALLBACK_STYLE) as React.CSSProperties
      }
      aria-live="polite"
    >
      <div className="ls-splash__route">
        <svg
          className="ls-splash__sea"
          viewBox="0 0 260 60"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d={SEA_PATH} pathLength="100" />
        </svg>
        <div className="ls-splash__ship-track">
          <img
            className="ls-splash__ship"
            src={SHIP_SRC}
            alt=""
            width={SHIP_W}
            height={SHIP_H}
            onError={hideBrokenShip}
          />
        </div>
      </div>

      {message ? (
        <div className="ls-splash__notice">
          <div className="ls-splash__notice-main">{message}</div>
          {subMessage ? <div className="ls-splash__notice-sub">{subMessage}</div> : null}
          {onRetry ? (
            <button type="button" className="ls-splash__refresh" onClick={onRetry}>
              刷新页面
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="ls-splash__tip">加载中，请稍后…</div>
          <div className="ls-splash__wordmark">LINSON MARINE</div>
        </>
      )}
    </output>
  );
};

export default AppSplash;